/**
 * DecisionBar — 投研笔记决策出口组件
 *
 * Sprint 4 升级：从状态标记升级为叙事笔记
 *
 * 点击【标记确信】→ 展开内联笔记输入框（一句话理由 + 标签）
 * 点击【加入观察】→ toggle 观察状态（轻量操作）
 * 点击【跳过】→ 清除该实体的笔记
 *
 * 笔记数据持久化到 explorationStore（localStorage）。
 */
import { useState, useRef, useEffect } from 'react';
import { useExplorationStore, type NoteTag, type NoteEntityType } from '../../stores/explorationStore';
import { useDataStore } from '../../stores/dataStore';
import { useIsMobile } from '../../hooks/useIsMobile';

// 交易所 → 货币映射
const CURRENCY_BY_EXCHANGE: Record<string, string> = {
  '美股': 'USD', '港股': 'HKD', '台股': 'TWD',
  '日股': 'JPY', '韩股': 'KRW', '欧股': 'EUR',
};

const TAG_OPTIONS: { value: NoteTag; label: string; emoji: string }[] = [
  { value: 'catalyst', label: '催化剂', emoji: '🚀' },
  { value: 'valuation', label: '估值', emoji: '📊' },
  { value: 'tech_lead', label: '技术领先', emoji: '⚡' },
  { value: 'industry_position', label: '产业地位', emoji: '🏰' },
];

interface DecisionBarProps {
  entityId: string;
  entityType: NoteEntityType;
}

export default function DecisionBar({ entityId, entityType }: DecisionBarProps) {
  const status = useExplorationStore(s => s.getStatus(entityId));
  const existingNote = useExplorationStore(s => s.getNote(entityId, entityType));
  const addNote = useExplorationStore(s => s.addNote);
  const updateNote = useExplorationStore(s => s.updateNote);
  const toggleWatchlist = useExplorationStore(s => s.toggleWatchlist);
  const clearSelection = useExplorationStore(s => s.clearSelection);
  const { companyIndex } = useDataStore();

  // 获取当前股价（仅公司实体，节点不记录股价）
  const currentStockPrice = entityType === 'company'
    ? companyIndex.get(entityId)?.latest_close
    : undefined;
  const currentStockCurrency = entityType === 'company'
    ? (CURRENCY_BY_EXCHANGE[companyIndex.get(entityId)?.exchange ?? ''] ?? 'CNY')
    : undefined;

  const [expanded, setExpanded] = useState(false);
  const [reason, setReason] = useState(existingNote?.reason ?? '');
  const [selectedTags, setSelectedTags] = useState<NoteTag[]>(existingNote?.tags ?? []);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const isMobile = useIsMobile();

  const isWatchlisted = status === 'watchlist';
  const isConviction = status === 'conviction';

  // React 19 渲染期同步模式：existingNote 变化时同步本地状态
  const [prevNoteId, setPrevNoteId] = useState(existingNote?.id ?? null);
  if (existingNote?.id !== prevNoteId) {
    setPrevNoteId(existingNote?.id ?? null);
    if (existingNote) {
      setReason(existingNote.reason);
      setSelectedTags(existingNote.tags);
    } else {
      setReason('');
      setSelectedTags([]);
    }
  }

  // 展开时自动聚焦
  useEffect(() => {
    if (expanded) {
      setTimeout(() => textareaRef.current?.focus(), 50);
    }
  }, [expanded]);

  const handleConviction = () => {
    if (!expanded) {
      setExpanded(true);
    } else {
      // 提交笔记
      if (reason.trim()) {
        if (existingNote?.status === 'conviction') {
          // 更新已有确信笔记（仅当有变化时）
          const reasonChanged = existingNote.reason !== reason.trim();
          const tagsChanged = JSON.stringify([...existingNote.tags].sort()) !== JSON.stringify([...selectedTags].sort());
          if (reasonChanged || tagsChanged) {
            updateNote(existingNote.id, { reason: reason.trim(), tags: selectedTags });
          }
        } else {
          // 新增确信笔记 — 记录当前股价作为基准（仅公司实体）
          addNote({
            entityId, entityType, status: 'conviction',
            reason: reason.trim(), tags: selectedTags,
            stockPrice: currentStockPrice,
            stockPriceCurrency: currentStockCurrency,
          });
        }
      }
      setExpanded(false);
      setReason('');
      setSelectedTags([]);
    }
  };

  const handleCancel = () => {
    setExpanded(false);
    setReason('');
    setSelectedTags([]);
  };

  const toggleTag = (tag: NoteTag) => {
    setSelectedTags(prev =>
      prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]
    );
  };

  const btnBase: React.CSSProperties = {
    flex: 1,
    padding: isMobile ? '8px 4px' : '9px 8px',
    border: '1px solid',
    borderRadius: 6,
    cursor: 'pointer',
    fontSize: isMobile ? 11 : 13,
    fontWeight: 600,
    transition: 'all 0.15s',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    minHeight: 38,
  };

  return (
    <div>
      {/* 展开的笔记输入区 */}
      {expanded && (
        <div style={{
          background: 'rgba(251,191,36,0.08)',
          border: '1px solid rgba(251,191,36,0.25)',
          borderRadius: 8,
          padding: '12px',
          marginBottom: 8,
        }}>
          {/* 理由输入 */}
          <textarea
            ref={textareaRef}
            value={reason}
            onChange={e => setReason(e.target.value)}
            placeholder="为什么确信？（一句话核心逻辑）"
            maxLength={200}
            style={{
              width: '100%', minHeight: 60, resize: 'none',
              background: 'rgba(15,23,42,0.8)', border: '1px solid #334155',
              borderRadius: 6, color: '#e2e8f0', fontSize: 12,
              padding: '8px 10px', lineHeight: 1.5,
              outline: 'none', fontFamily: 'inherit',
            }}
            onFocus={e => { e.target.style.borderColor = '#fbbf24'; }}
            onBlur={e => { e.target.style.borderColor = '#334155'; }}
          />
          <div style={{ fontSize: 10, color: '#64748b', textAlign: 'right', marginTop: 2 }}>
            {reason.length}/200
          </div>

          {/* 标签选择 */}
          <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
            {TAG_OPTIONS.map(tag => (
              <button
                key={tag.value}
                onClick={() => toggleTag(tag.value)}
                style={{
                  padding: '3px 8px', borderRadius: 12, fontSize: 11,
                  border: selectedTags.includes(tag.value)
                    ? '1px solid #fbbf24' : '1px solid #334155',
                  background: selectedTags.includes(tag.value)
                    ? 'rgba(251,191,36,0.15)' : 'transparent',
                  color: selectedTags.includes(tag.value) ? '#fbbf24' : '#94a3b8',
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                }}
              >
                {tag.emoji} {tag.label}
              </button>
            ))}
          </div>

          {/* 确认/取消按钮 */}
          <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
            <button
              onClick={handleConviction}
              disabled={!reason.trim()}
              style={{
                flex: 1, padding: '7px', borderRadius: 6, fontSize: 12, fontWeight: 600,
                border: 'none',
                background: reason.trim() ? '#fbbf24' : 'rgba(251,191,36,0.2)',
                color: reason.trim() ? '#1e293b' : '#92400e',
                cursor: reason.trim() ? 'pointer' : 'not-allowed',
                opacity: reason.trim() ? 1 : 0.5,
              }}
            >
              ◆ 记录确信
            </button>
            <button
              onClick={handleCancel}
              style={{
                flex: 1, padding: '7px', borderRadius: 6, fontSize: 12,
                border: '1px solid #334155', background: 'transparent',
                color: '#94a3b8', cursor: 'pointer',
              }}
            >
              取消
            </button>
          </div>
        </div>
      )}

      {/* 三个动作按钮 */}
      <div style={{
        display: 'flex', gap: 8,
        paddingTop: expanded ? 0 : 0,
      }} role="group" aria-label="节点决策">
        <button
          onClick={() => toggleWatchlist(entityId, entityType)}
          aria-pressed={isWatchlisted}
          style={{
            ...btnBase,
            background: isWatchlisted ? '#10b981' : 'transparent',
            borderColor: isWatchlisted ? '#10b981' : '#334155',
            color: isWatchlisted ? '#fff' : '#94a3b8',
          }}
        >
          {isWatchlisted ? (isMobile ? '✓ 观察' : '✓ 观察中') : (isMobile ? '观察' : '加入观察')}
        </button>
        <button
          onClick={handleConviction}
          aria-pressed={isConviction}
          style={{
            ...btnBase,
            background: isConviction ? '#fbbf24' : 'transparent',
            borderColor: isConviction ? '#fbbf24' : '#334155',
            color: isConviction ? '#1e293b' : '#94a3b8',
          }}
        >
          {isConviction
            ? (expanded ? (isMobile ? '◆ 更新' : '◆ 更新确信') : (isMobile ? '◆ 确信' : '◆ 已确信'))
            : (expanded
              ? <span aria-label="保存笔记" role="img">✓</span>
              : (isMobile ? '确信' : '标记确信'))}
        </button>
        <button
          onClick={() => {
            if (existingNote?.reason && !confirm('该笔记有理由内容，确认清除？')) return;
            clearSelection(entityId, entityType);
          }}
          aria-label="清除笔记"
          style={{
            ...btnBase,
            background: 'transparent',
            borderColor: '#334155',
            color: '#64748b',
          }}
          title="清除此实体的所有笔记"
        >
          {isMobile ? '✕' : '清除'}
        </button>
      </div>

      {/* 已有笔记预览 */}
      {existingNote && !expanded && (
        <div style={{
          marginTop: 6, padding: '6px 8px', borderRadius: 4,
          background: 'rgba(15,23,42,0.5)', border: '1px solid #1e293b',
          fontSize: 11, color: '#94a3b8',
        }}>
          <div style={{ color: '#fbbf24', fontWeight: 600, marginBottom: 2 }}>
            ◆ {new Date(existingNote.timestamp).toLocaleDateString('zh-CN')}
          </div>
          {existingNote.reason && <div>{existingNote.reason}</div>}
          {existingNote.tags.length > 0 && (
            <div style={{ display: 'flex', gap: 4, marginTop: 3, flexWrap: 'wrap' }}>
              {existingNote.tags.map(t => {
                const opt = TAG_OPTIONS.find(o => o.value === t);
                return opt ? (
                  <span key={t} style={{ fontSize: 10 }}>{opt.emoji}</span>
                ) : null;
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
